// Catalog.ID Client — Agent Tokens Sub-Pane
// ============================================================================
// Lists refresh tokens for an agent. Revoking one is a verb about ONE ROW, so
// it rides a button in that row — never the row click, which used to revoke
// the token the user merely wanted to read.

import { createListPaneType } from '@catalog-shell';
import type { ListPaneConfig, PaneTypeRenderContext } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import * as agentApi from '../api/agent';
import type { RefreshTokenSummary } from '../api/types';
import { createRowActionColumn, type RowActionSpec } from '@catalog-shell';
import { PANE_IDS } from '../shell-config';

const columns: ColumnDef<RefreshTokenSummary>[] = [
    {
        key: 'refreshTokenId',
        header: 'Token ID',
        width: 80,
        getValue: (item) => item.refreshTokenId,
        type: { kind: 'text' },
    },
    {
        key: 'scopes',
        header: 'Scopes',
        width: 160,
        getValue: (item) => item.scopes,
        type: { kind: 'text' },
    },
    {
        key: 'isRevoked',
        header: 'Status',
        width: 80,
        getValue: (item) => item.isRevoked ? 'Revoked' : 'Active',
        type: {
            kind: 'badge',
            backgroundColor: (v) => (v as string) === 'Active' ? 'rgba(80,200,80,0.3)' : 'rgba(220,50,50,0.3)',
        },
    },
    {
        key: 'creationTime',
        header: 'Created',
        width: 100,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
    },
    {
        key: 'expiresAt',
        header: 'Expires',
        width: 100,
        getValue: (item) => item.expiresAt,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
    },
];

function createTokensDataSource(
    agentCid: string,
    remember: (items: RefreshTokenSummary[]) => void,
): DataSource<RefreshTokenSummary> {
    return new DataSource<RefreshTokenSummary>({
        fetch: async (params): Promise<FetchResult<RefreshTokenSummary>> => {
            const tokens = await agentApi.listRefreshTokens(agentCid);
            const sliced = tokens.slice(params.offset, params.offset + params.limit);
            remember(sliced);
            return { items: sliced, totalCount: tokens.length };
        },
        keyFn: (item) => String(item.refreshTokenId),
        pageSize: 100,
    });
}

/**
 * Pure: what a token's row offers. A revoked token is already dead — offering
 * to revoke it again would be a button that can only fail.
 */
export function planRefreshTokenRowAction(item: RefreshTokenSummary): RowActionSpec | null {
    if (item.isRevoked) return null;
    return { label: 'Revoke', variant: 'danger' };
}

/**
 * The column set is shared by every instance of this pane type, while the agent
 * whose tokens are listed (and the shell to talk to) belong to the instance.
 * The fetch that produced a row records both against the row object — the only
 * thing a cell click has in hand.
 */
const rowOrigin = new WeakMap<RefreshTokenSummary, { shell: PaneTypeRenderContext['shell']; agentCid: string }>();

const revokeColumn = createRowActionColumn<RefreshTokenSummary>({
    plan: planRefreshTokenRowAction,
    execute: (item) => {
        const origin = rowOrigin.get(item);
        if (!origin) return;
        void handleRevokeToken(origin.shell, item, origin.agentCid);
    },
});

export function createAgentTokensPane() {
    const config: ListPaneConfig<RefreshTokenSummary> = {
        typeId: PANE_IDS.agentTokens,
        defaultTitle: 'Refresh Tokens',
        createDataSource: (ctx) => {
            const agentCid = ctx.instance.context['agentCid'] as string;
            return createTokensDataSource(agentCid, (items) => {
                for (const item of items) rowOrigin.set(item, { shell: ctx.shell, agentCid });
            });
        },
        columns: [...columns, revokeColumn],
        keyFn: (item) => String(item.refreshTokenId),
        initialMode: 'table',
        enabledModes: ['table'],
        selectionMode: 'none',
        onCreated: ({ dataSource }, ctx) => {
            ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
                if ((data as { type?: string })?.type === 'agents') {
                    dataSource.refresh();
                }
            }));
        },
        // No onItemSelect: a click on a row selects it and nothing more. The
        // row's verb is the Revoke button in the row itself.
        ariaLabel: 'Agent refresh tokens',
    };
    return createListPaneType(config);
}

async function handleRevokeToken(
    shell: PaneTypeRenderContext['shell'],
    item: RefreshTokenSummary,
    agentCid: string,
): Promise<void> {
    const ok = await shell.confirm({
        title: 'Revoke Token',
        message: `Revoke refresh token #${item.refreshTokenId}? The agent will need a new token to authenticate.`,
        confirmLabel: 'Revoke',
        confirmVariant: 'no',
    });
    if (!ok) return;
    try {
        await agentApi.revokeRefreshToken(agentCid, item.refreshTokenId);
        shell.emit('data-changed', { type: 'agents' });
        shell.toast('Token revoked', 'success');
    } catch (err) {
        shell.toast(err instanceof Error ? err.message : 'Failed to revoke token', 'error');
    }
}
